import React, { useEffect } from 'react';
import {
  HStack,
  Badge,
  Text,
  Tooltip,
  Wrap,
  WrapItem
} from '@chakra-ui/react';

const MonthSelector = ({
  months,
  selectedMonth,
  onMonthChange,
  isDisabled = false
}) => {
  // Get current month function
  const getCurrentMonth = () => {
    const date = new Date();
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return monthNames[date.getMonth()];
  };

  // Sort months in financial year order
  const monthOrder = [
    "April", "May", "June", "July", "August", "September",
    "October", "November", "December", "January", "February", "March"
  ];

  // Log for debugging
  useEffect(() => {
    console.log('MonthSelector Debug:', {
      months,
      selectedMonth,
      currentMonth: getCurrentMonth(),
      isDisabled
    });
  }, [months, selectedMonth]);

  // Set default month on mount if no month is selected
  useEffect(() => {
    if (months && months.length > 0 && !selectedMonth && !isDisabled) {
      const currentMonth = getCurrentMonth();
      const sortedMonths = [...months].sort((a, b) => {
        return monthOrder.indexOf(a) - monthOrder.indexOf(b);
      });
      
      console.log('Setting default month:', {
        currentMonth,
        sortedMonths,
        includes: sortedMonths.includes(currentMonth)
      });
      
      // Check if current month is in the available months
      if (sortedMonths.includes(currentMonth)) {
        onMonthChange(currentMonth);
      } else {
        // If current month not available, select first available month
        onMonthChange(sortedMonths[0]);
      }
    }
  }, [months, selectedMonth, isDisabled, onMonthChange]);

  // Early return after all hooks
  if (!months || months.length === 0) return null;
  
  const sortedMonths = [...months].sort((a, b) => {
    return monthOrder.indexOf(a) - monthOrder.indexOf(b);
  });

  const currentMonth = getCurrentMonth();

  return (
    <HStack spacing={3} align="center" flexWrap="wrap">
      <Text fontSize="sm" fontWeight="medium" color="gray.600" whiteSpace="nowrap">
        Month:
      </Text>
      <Wrap spacing={2}>
        {sortedMonths.map(month => {
          const isCurrentMonth = month === currentMonth;
          const isSelected = month === selectedMonth;
          
          return (
            <WrapItem key={month}>
              <Tooltip 
                label={`View data for ${month}${isCurrentMonth ? ' (Current Month)' : ''}`} 
                placement="top"
              >
                <Badge
                  colorScheme={isSelected ? 'blue' : isCurrentMonth ? 'teal' : 'gray'}
                  cursor={isDisabled ? 'not-allowed' : 'pointer'}
                  px={4}
                  py={2}
                  borderRadius="full"
                  onClick={() => !isDisabled && onMonthChange(month)}
                  opacity={isDisabled ? 0.5 : 1}
                  bg={
                    isSelected 
                      ? 'blue.500' 
                      : isCurrentMonth && !isSelected
                        ? 'teal.100'
                        : 'gray.200'
                  }
                  color={
                    isSelected 
                      ? 'white' 
                      : isCurrentMonth && !isSelected
                        ? 'teal.800'
                        : 'gray.700'
                  }
                  fontWeight={isSelected ? 'bold' : isCurrentMonth ? '600' : 'normal'}
                  fontSize="sm"
                  boxShadow={isSelected ? 'md' : isCurrentMonth ? 'sm' : 'none'}
                  border={isCurrentMonth && !isSelected ? '1px solid' : 'none'}
                  borderColor="teal.400"
                  _hover={!isDisabled && {
                    transform: 'scale(1.05)',
                    bg: isSelected 
                      ? 'blue.600' 
                      : isCurrentMonth 
                        ? 'teal.200' 
                        : 'gray.300',
                  }}
                  transition="all 0.2s"
                >
                  {month.substring(0, 3).toUpperCase()}
                  {isCurrentMonth && !isSelected && (
                    <Text as="span" ml={1} fontSize="10px">●</Text>
                  )}
                </Badge>
              </Tooltip>
            </WrapItem>
          );
        })}
      </Wrap>
      {selectedMonth && (
        <Badge colorScheme="green" variant="solid" px={3} py={1} borderRadius="full" whiteSpace="nowrap">
          Active: {selectedMonth}
          {selectedMonth === currentMonth && ' (Current)'}
        </Badge>
      )}
    </HStack>
  );
};

export default MonthSelector;